import { CartaoDeGrafico } from '../../design/graficos/CartaoDeGrafico';
import { agrupar } from '../../design/graficos/agrupar';
import {
  MODELOS_META,
  MODELOS_PARTES,
  MODELOS_QUANDO,
  MODELOS_TEMPO,
  contraMeta,
  fmt,
  partes,
  quandoVende,
  serieNoTempo,
} from '../../design/graficos/modelos';
import type { GaleriaDeGraficos } from '../../paineis';

/**
 * OS GRÁFICOS DO DASHBOARD — o mesmo motor da seção Gráficos (troca de modelo, número, conclusão),
 * num recorte para decidir rápido: o movimento do período (ou do dia, por hora), a meta da equipe,
 * a premiação por programa, quem mais vendeu e quando a loja vende. Dados reais da galeria.
 */

const PROGRAMAS = [
  { id: 'vitamina' as const, nome: 'Vitamina', cor: '#1a7f55' },
  { id: 'pre_vencido' as const, nome: 'Pré-vencido', cor: '#cb1b2b' },
  { id: 'paradao' as const, nome: 'Paradão', cor: '#b7791f' },
];
const DIAS = ['domingo', 'segunda', 'terça', 'quarta', 'quinta', 'sexta', 'sábado'];
const diaCurto = (d: string) => `${d.slice(8, 10)}/${d.slice(5, 7)}`;
const soma = (xs: readonly number[]) => xs.reduce((a, b) => a + b, 0);
const variacao = (a: number, b: number) => (b > 0 ? ((a - b) / b) * 100 : null);
const primeiroNome = (s: string) => {
  const p = s.trim().split(/\s+/)[0] ?? s;
  return p.charAt(0).toUpperCase() + p.slice(1).toLowerCase();
};
const FONTE_HOS = 'Fonte: vendas do HOS (API, automática)';
const FONTE_INOVA = 'Fonte: lançamentos no InovaPay';

/** O gráfico grande do meio: o período dia a dia, ou — num dia só — hora a hora. */
export function GraficoPrincipal({ g }: { g: GaleriaDeGraficos }) {
  const umDia = g.periodo.de === g.periodo.ate;
  const fat = soma(g.diario.map((x) => x.faturamento));
  const fatAnt = soma(g.diarioAnterior.map((x) => x.faturamento));
  const cupons = soma(g.diario.map((x) => x.cupons));
  if (umDia) {
    const pico = [...g.porHora].sort((a, b) => b.cupons - a.cupons)[0];
    return (
      <CartaoDeGrafico
        id="db-hora"
        titulo={`Vendas hora a hora · ${diaCurto(g.periodo.de)}`}
        numero={fmt(fat, 'reais')}
        variacao={{ pct: variacao(fat, fatAnt), bom: 'subir' }}
        conclusao={
          pico ? (
            <>
              <strong>{fmt(cupons, 'inteiro')}</strong> cupons no dia; o horário mais forte foi{' '}
              <strong>{String(pico.hora)}h</strong> ({fmt(pico.cupons, 'inteiro')} cupons).
              Comparado com {diaCurto(g.anterior.de)}.
            </>
          ) : (
            'Nenhuma venda do HOS neste dia ainda.'
          )
        }
        modelos={MODELOS_QUANDO.filter(([m]) => m !== 'calor')}
        padrao="colunas_hora"
        desenhar={(m) => quandoVende(m, { mapa: g.mapaDeCalor, porHora: g.porHora })}
        fonte={FONTE_HOS}
        vazio={cupons ? null : 'Nenhuma venda do HOS neste dia ainda.'}
        altura={290}
      />
    );
  }
  const melhor = [...g.diario].sort((a, b) => b.faturamento - a.faturamento)[0];
  return (
    <CartaoDeGrafico
      id="db-periodo"
      titulo="Faturamento no período"
      numero={fmt(fat, 'reais')}
      variacao={{ pct: variacao(fat, fatAnt), bom: 'subir' }}
      conclusao={
        <>
          {fmt(cupons, 'inteiro')} cupons, ticket médio{' '}
          <strong>{fmt(cupons ? fat / cupons : null, 'reais')}</strong>. Melhor dia:{' '}
          <strong>
            {melhor ? `${diaCurto(melhor.dia)} (${fmt(melhor.faturamento, 'reais')})` : '—'}
          </strong>
          . Tracejado: o período anterior.
        </>
      }
      modelos={MODELOS_TEMPO}
      padrao="combo"
      desenhar={(m) =>
        serieNoTempo(m, {
          cats: agrupar(g.diario).map((x) => x.rotulo),
          atual: agrupar(g.diario).map((x) => x.faturamento),
          anterior: agrupar(g.diarioAnterior).map((x) => x.faturamento),
          nome: 'Faturamento',
          formato: 'reais',
          linha: {
            nome: 'Ticket médio',
            valores: agrupar(g.diario).map((x) => x.ticket),
            formato: 'reais',
          },
        })
      }
      fonte={FONTE_HOS}
      vazio={cupons ? null : 'Nenhuma venda do HOS no período.'}
      altura={290}
    />
  );
}

/** A grade de baixo: meta, programas, equipe e quando vende. */
export function GradeDoDashboard({ g }: { g: GaleriaDeGraficos }) {
  const alvo = g.meta.itensPorPessoa;
  const bateram = g.meta.pessoas.filter((p) => p.itens >= alvo).length;
  const pct = g.meta.pessoas.length ? (bateram / g.meta.pessoas.length) * 100 : 0;
  const porPrograma = PROGRAMAS.map((p) => ({
    ...p,
    premio: soma(g.programas.filter((x) => x.programa === p.id).map((x) => x.premio)),
  }));
  const premio = soma(porPrograma.map((p) => p.premio));
  const lider = [...porPrograma].sort((a, b) => b.premio - a.premio)[0];
  const fat = soma(g.diario.map((x) => x.faturamento));
  const top = g.porBalconista.slice(0, 8);
  const pico = [...g.porHora].sort((a, b) => b.cupons - a.cupons)[0];
  const porDow = Array.from({ length: 7 }, (_, dow) =>
    soma(g.mapaDeCalor.filter((x) => x.dow === dow).map((x) => x.cupons)),
  );
  const melhorDow = porDow.indexOf(Math.max(...porDow));
  const temVenda = g.diario.some((x) => x.cupons > 0);

  return (
    <div className="gx-grade db-grade-graficos">
      <CartaoDeGrafico
        id="db-meta"
        titulo="Meta da equipe"
        numero={
          g.meta.pessoas.length
            ? `${String(bateram)} de ${String(g.meta.pessoas.length)} bateram`
            : 'sem lançamento'
        }
        conclusao={
          <>
            Semana {diaCurto(g.meta.semana.de)} a {diaCurto(g.meta.semana.ate)}:{' '}
            <strong>{fmt(pct, 'pct')}</strong> da equipe fez os {String(alvo)} itens dos programas.
          </>
        }
        modelos={MODELOS_META}
        padrao="velocimetro"
        desenhar={(m) =>
          contraMeta(m, {
            itens: g.meta.pessoas.map((p) => ({ nome: primeiroNome(p.nome), valor: p.itens })),
            alvo,
            unidade: 'itens',
            resumo: { nome: 'bateram a meta', pct },
          })
        }
        fonte={FONTE_INOVA}
        vazio={g.meta.pessoas.length ? null : 'Nenhum item de programa lançado na semana.'}
        altura={250}
      />
      <CartaoDeGrafico
        id="db-programas"
        titulo="Premiação por programa"
        numero={fmt(premio, 'reais')}
        conclusao={
          premio ? (
            <>
              <strong>{lider?.nome}</strong> gerou{' '}
              {fmt(lider ? (lider.premio / premio) * 100 : 0, 'pct')} da premiação do período.
            </>
          ) : (
            'Nenhuma premiação lançada no período.'
          )
        }
        modelos={MODELOS_PARTES}
        padrao="rosca"
        desenhar={(m) =>
          partes(m, {
            itens: porPrograma
              .filter((p) => p.premio > 0)
              .map((p) => ({ nome: p.nome, valor: p.premio, cor: p.cor })),
            formato: 'reais',
          })
        }
        fonte={FONTE_INOVA}
        vazio={premio ? null : 'Nenhuma premiação lançada no período.'}
        altura={250}
      />
      <CartaoDeGrafico
        id="db-equipe"
        titulo="Quem mais vendeu"
        numero={top[0] ? `${primeiroNome(top[0].nome)} · ${fmt(top[0].faturamento, 'reais')}` : '—'}
        conclusao={
          top.length && fat ? (
            <>
              Os {String(top.length)} primeiros fizeram{' '}
              <strong>{fmt((soma(top.map((p) => p.faturamento)) / fat) * 100, 'pct')}</strong> do
              faturamento do período.
            </>
          ) : (
            'Nenhuma venda do HOS no período.'
          )
        }
        modelos={MODELOS_PARTES}
        padrao="barras"
        desenhar={(m) =>
          partes(m, {
            itens: top.map((p) => ({ nome: primeiroNome(p.nome), valor: p.faturamento })),
            formato: 'reais',
          })
        }
        fonte={FONTE_HOS}
        vazio={top.length ? null : 'Nenhuma venda do HOS no período.'}
        altura={250}
      />
      <CartaoDeGrafico
        id="db-quando"
        titulo="Quando a loja vende"
        numero={pico ? `pico às ${String(pico.hora)}h` : '—'}
        conclusao={
          pico ? (
            <>
              Horário mais forte: <strong>{String(pico.hora)}h</strong>
              {g.periodo.de !== g.periodo.ate ? (
                <>
                  ; dia mais forte: <strong>{DIAS[melhorDow] ?? '—'}</strong>
                </>
              ) : null}
              .
            </>
          ) : (
            'Nenhuma venda do HOS no período.'
          )
        }
        modelos={MODELOS_QUANDO}
        padrao={g.periodo.de === g.periodo.ate ? 'radial_hora' : 'calor'}
        desenhar={(m) => quandoVende(m, { mapa: g.mapaDeCalor, porHora: g.porHora })}
        fonte={FONTE_HOS}
        vazio={temVenda ? null : 'Nenhuma venda do HOS no período.'}
        altura={250}
      />
    </div>
  );
}
